import {
  Box,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  Skeleton,
} from "@chakra-ui/react";
import _ from "lodash";
import React, { useMemo } from "react";
import { RiMore2Fill } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithConfig } from "src/components/Permissions/WithConfig";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { FormTabsTypes, SearchAndFilterTypes } from "src/constants";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useGlossary } from "src/hooks/useGlossary";
import { useChunkedRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import * as List from "src/services/list";
import { isNonEmptyString } from "src/services/predicates";
import { FormTemplateSettings } from "src/types/formTemplate/v2";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  FETCH_OFFER_WAITLIST_STATUS_MAX_INPUT_SIZE,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { AcceptOffer } from "./AcceptOffer";
import { AddToWaitlists } from "./AddToWaitlists";
import { BulkAcceptOffer } from "./BulkAcceptOffer";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { BulkAddToWaitlists } from "./BulkAddToWaitlists";
import { BulkClearSubstatus } from "./BulkClearSubstatus";
import { BulkDeclineOffer } from "./BulkDeclineOffer";
import { BulkExcludeForm } from "./BulkExcludeForm";
import { BulkExecuteFormRules } from "./BulkExecuteFormRules";
import { BulkMakeOffer } from "./BulkMakeOffer";
import { BulkRemoveFromWaitlists } from "./BulkRemoveFromWaitlists";
import { BulkRevokeOffer } from "./BulkRevokeOffer";
import { ClearOfferExpiration } from "./ClearOfferExpiration";
import { ClearSubstatus } from "./ClearSubstatus";
import { DeclineOffer } from "./DeclineOffer";
import { ExcludeForm } from "./ExcludeForm";
import { ExecuteFormRules } from "./ExecuteFormRules";
import { MakeOffer } from "./MakeOffer";
import { MenuItem } from "./menuItem";
import { RemoveFromWaitlists } from "./RemoveFromWaitlists";
import { RequestPayment } from "./RequestPayment";
import { RevokeOffer } from "./RevokeOffer";
import { RunMatch } from "./RunMatch";
import { RunMatchFormService } from "./RunMatchFormService";

const AuthorizedMatchContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <WithUserPermissions permissions={["match:create"]}>
      <WithConfig configType="Match">{children}</WithConfig>
    </WithUserPermissions>
  );
};

type Props = {
  formTemplateId: string;
  hiddenMenuItems: MenuItem[];
  fetchSelectedFormKeys: () => Promise<SchoolFormId[]>;
  onRefetch: () => void;
};

export const MoreMenu: React.FC<React.PropsWithChildren<Props>> = ({
  formTemplateId,
  hiddenMenuItems,
  fetchSelectedFormKeys,
  onRefetch,
  children,
}) => {
  const userPermissions = useUserPermissions();
  const hasMatchPermissions = userPermissions.hasSome(["match:create"]);
  const hasOfferUpdatePermissions = userPermissions.hasSome(["offer:update"]);

  const hasVisibleItems = React.useMemo(() => {
    const hasChildren = React.Children.count(children) > 0;
    const hasOffersItems =
      !hiddenMenuItems.includes(MenuItem.Offers) && hasOfferUpdatePermissions;
    const hasMatchItems = hasMatchPermissions;

    return hasChildren || hasOffersItems || hasMatchItems;
  }, [
    children,
    hiddenMenuItems,
    hasOfferUpdatePermissions,
    hasMatchPermissions,
  ]);

  if (!hasVisibleItems) {
    return null;
  }

  return (
    <MoreMenuContent
      formTemplateId={formTemplateId}
      hiddenMenuItems={hiddenMenuItems}
      fetchSelectedFormKeys={fetchSelectedFormKeys}
      onRefetch={onRefetch}
    >
      {children}
    </MoreMenuContent>
  );
};

const MenuItemSkeleton: React.FC = () => (
  <Box paddingX={3} paddingY={1.5}>
    <Skeleton height={5} />
  </Box>
);

const MenuSkeleton: React.FC = () => {
  const { glossary } = useGlossary();

  return (
    <>
      <MenuGroup title="Waitlists">
        <MenuItemSkeleton />
        <MenuItemSkeleton />
      </MenuGroup>
      <Divider />
      <MenuGroup title="Offers">
        <MenuItemSkeleton />
        <MenuItemSkeleton />
        <MenuItemSkeleton />
        <MenuItemSkeleton />
        <MenuGroup>
          <Divider />
          <MenuItemSkeleton />
        </MenuGroup>
      </MenuGroup>
      <MenuGroup>
        <MenuItemSkeleton />
        <MenuItemSkeleton />
      </MenuGroup>
      <Divider />
      <MenuGroup title={glossary`Match`}>
        <MenuItemSkeleton />
      </MenuGroup>
      <Divider />
      <MenuGroup title="Admin Actions">
        <MenuItemSkeleton />
      </MenuGroup>
    </>
  );
};

type AdmissionsMenuGroupProps = {
  asyncBulkActions: boolean;
  formTemplateSettings: Pick<FormTemplateSettings, "allowDeclineAcceptedOffer">;
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch: () => void;
};

const AdmissionsMenuGroup: React.FC<AdmissionsMenuGroupProps> = ({
  asyncBulkActions,
  formTemplateSettings,
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
}) => {
  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get(SearchAndFilterTypes.Tab);

  return (
    <>
      {selectedTab !== FormTabsTypes.Offers && (
        <>
          <MenuGroup title="Waitlists">
            {(selectedTab === FormTabsTypes.All ||
              selectedTab === FormTabsTypes.Submissions ||
              selectedTab === FormTabsTypes.FormListImports) &&
              (asyncBulkActions ? (
                <BulkAddToWaitlists
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                />
              ) : (
                <AddToWaitlists
                  onRefetch={onRefetch}
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  isBulkAction
                />
              ))}

            {(selectedTab === FormTabsTypes.All ||
              selectedTab === FormTabsTypes.Waitlists ||
              selectedTab === FormTabsTypes.FormListImports) &&
              (asyncBulkActions ? (
                <BulkRemoveFromWaitlists
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                />
              ) : (
                <RemoveFromWaitlists
                  selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                  selectedFormSchools={selectedFormSchools}
                  onRefetch={onRefetch}
                  isBulkAction
                />
              ))}
          </MenuGroup>
          <Divider />
        </>
      )}
      <MenuGroup title="Offers">
        {asyncBulkActions ? (
          <BulkMakeOffer
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
            selectedFormSchools={selectedFormSchools}
          />
        ) : (
          <MakeOffer
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
            selectedFormSchools={selectedFormSchools}
            onRefetch={onRefetch}
            isBulkAction
          />
        )}
        {(selectedTab === FormTabsTypes.All ||
          selectedTab === FormTabsTypes.Offers ||
          selectedTab === FormTabsTypes.FormListImports) &&
          (asyncBulkActions ? (
            <BulkAcceptOffer
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
            />
          ) : (
            <AcceptOffer
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
              onRefetch={onRefetch}
              isBulkAction
            />
          ))}
        {(selectedTab === FormTabsTypes.All ||
          selectedTab === FormTabsTypes.Offers ||
          selectedTab === FormTabsTypes.FormListImports) &&
          (asyncBulkActions ? (
            <BulkDeclineOffer
              formTemplateSettings={formTemplateSettings}
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
            />
          ) : (
            <DeclineOffer
              formTemplateSettings={formTemplateSettings}
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
              onRefetch={onRefetch}
              isBulkAction
            />
          ))}
        {(selectedTab === FormTabsTypes.All ||
          selectedTab === FormTabsTypes.Offers ||
          selectedTab === FormTabsTypes.FormListImports) &&
          (asyncBulkActions ? (
            <BulkRevokeOffer
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
            />
          ) : (
            <RevokeOffer
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
              onRefetch={onRefetch}
              isBulkAction
            />
          ))}
        {(selectedTab === FormTabsTypes.All ||
          selectedTab === FormTabsTypes.Offers ||
          selectedTab === FormTabsTypes.FormListImports) && (
          <MenuGroup>
            <Divider />
            <ClearOfferExpiration
              selectedFormSchoolStatuses={selectedFormSchoolStatuses}
              selectedFormSchools={selectedFormSchools}
              onRefetch={onRefetch}
              isBulkAction
            />
          </MenuGroup>
        )}
      </MenuGroup>
      <MenuGroup>
        {asyncBulkActions ? (
          <BulkExcludeForm
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
          />
        ) : (
          <ExcludeForm
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
            onRefetch={onRefetch}
            isBulkAction
          />
        )}
        {asyncBulkActions ? (
          <BulkClearSubstatus
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
          />
        ) : (
          <ClearSubstatus
            selectedFormSchoolStatuses={selectedFormSchoolStatuses}
            onRefetch={onRefetch}
            isBulkAction
          />
        )}
      </MenuGroup>
    </>
  );
};

const MoreMenuContent: React.FC<
  React.PropsWithChildren<
    Pick<
      Props,
      | "formTemplateId"
      | "hiddenMenuItems"
      | "fetchSelectedFormKeys"
      | "onRefetch"
    >
  >
> = ({
  formTemplateId,
  hiddenMenuItems,
  fetchSelectedFormKeys,
  onRefetch,
  children,
}) => {
  const { loadTargets } = useBulkActionTargets();
  const flags = useFlags(["async-bulk-actions", "form-service-match"]);
  const asyncBulkActions = flags["async-bulk-actions"].enabled;
  const formServiceMatch = flags["form-service-match"].enabled;
  const [selectedFormSchoolsRD, setSelectedFormSchoolsRD] = React.useState<
    RD.RemoteData<unknown, SchoolFormId[]>
  >(RD.loading());

  const { glossary } = useGlossary();

  const searchKeys: GQL.form_school_rank_bool_exp[] = React.useMemo(
    () =>
      List.filterMap(selectedFormSchoolsRD.withDefault([]), (schoolFormId) => {
        if (!schoolFormId.schoolId) return null;

        return {
          form_id: { _eq: schoolFormId.formId },
          school_id: { _eq: schoolFormId.schoolId },
        };
      }),
    [selectedFormSchoolsRD]
  );

  const { data: formTemplateSettingsQuery } = FSQ.useGetFormTemplateSettings(
    {
      path: {
        formTemplateId,
      },
    },
    [formTemplateId],
    {
      enabled: !!formTemplateId,
    }
  );

  const formTemplateSettings = useMemo(() => {
    return (
      formTemplateSettingsQuery?.data ?? {
        allowDeclineAcceptedOffer: "none" as FSR.AllowDeclineAcceptedOffer,
      }
    );
  }, [formTemplateSettingsQuery?.data]);

  const { remoteData: statusData } = useChunkedRemoteDataQuery<
    GQL.FetchOfferWaitlistStatus,
    GQL.FetchOfferWaitlistStatusVariables
  >(
    FETCH_OFFER_WAITLIST_STATUS,
    React.useMemo(
      () =>
        _.chunk(searchKeys, FETCH_OFFER_WAITLIST_STATUS_MAX_INPUT_SIZE).map(
          (chunk) => ({ search_keys: chunk })
        ),
      [searchKeys]
    ),
    {
      skip: !selectedFormSchoolsRD.hasData(),
      fetchPolicy: "cache-and-network",
    }
  );

  const selectedFormSchoolStatusesRD = React.useMemo(() => {
    return statusData.map((data) =>
      data.flatMap((dataChunk) => dataChunk.form_school_rank)
    );
  }, [statusData]);

  const onOpen = React.useCallback(async () => {
    setSelectedFormSchoolsRD(RD.loading());
    try {
      const items = asyncBulkActions
        ? await loadTargets()
        : await fetchSelectedFormKeys();
      setSelectedFormSchoolsRD(RD.success(items));
    } catch (error) {
      setSelectedFormSchoolsRD(
        RD.failure(
          error instanceof Error ? error : new Error("Failed to load selection")
        )
      );
    }
  }, [asyncBulkActions, fetchSelectedFormKeys, loadTargets]);

  return (
    <Menu onOpen={onOpen}>
      <MenuButton
        as={Button}
        variant="banner"
        size="sm"
        leftIcon={<RiMore2Fill />}
      >
        More
      </MenuButton>
      <MenuList zIndex="popover">
        {
          // Show children that are not visible on the TableBanner
          React.Children.count(children) > 0 && (
            <MenuGroup>{children}</MenuGroup>
          )
        }
        <RemoteDataView
          remoteData={RD.toTuple(
            selectedFormSchoolStatusesRD,
            selectedFormSchoolsRD
          )}
          loading={<MenuSkeleton />}
          notAsked={<MenuSkeleton />}
          error={() => <GenericError />}
          config={{ showDataWhileReloading: false }}
        >
          {([selectedFormSchoolStatuses, selectedFormSchools]) => (
            <>
              <WithUserPermissions
                permissions={["payments:create", "payments:read"]}
                permissionMatch="all"
              >
                <WithConfig<OrgConfig.Payments>
                  configType="Payments"
                  predicate={(paymentConfig) =>
                    paymentConfig?.enabled === true &&
                    isNonEmptyString(paymentConfig.accountId)
                  }
                >
                  <MenuGroup>
                    <RequestPayment
                      selectedFormSchools={selectedFormSchools}
                      onRefetch={onRefetch}
                    />
                  </MenuGroup>
                </WithConfig>
              </WithUserPermissions>
              {!hiddenMenuItems.includes(MenuItem.Offers) && <Divider />}
              {!hiddenMenuItems.includes(MenuItem.Offers) && (
                <WithUserPermissions permissions={["offer:update"]}>
                  <AdmissionsMenuGroup
                    asyncBulkActions={asyncBulkActions}
                    formTemplateSettings={formTemplateSettings}
                    selectedFormSchoolStatuses={selectedFormSchoolStatuses}
                    selectedFormSchools={selectedFormSchools}
                    onRefetch={onRefetch}
                  />
                  <AuthorizedMatchContainer>
                    <Divider />
                  </AuthorizedMatchContainer>
                </WithUserPermissions>
              )}
              <AuthorizedMatchContainer>
                <MenuGroup title={glossary`Match`}>
                  {formServiceMatch ? (
                    <RunMatchFormService
                      selectedFormSchools={selectedFormSchools}
                    />
                  ) : (
                    <RunMatch
                      formTemplateId={formTemplateId}
                      selectedFormSchools={selectedFormSchools}
                    />
                  )}
                </MenuGroup>
              </AuthorizedMatchContainer>
              <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
                <Divider />
                <MenuGroup title="Admin Actions">
                  {asyncBulkActions ? (
                    <BulkExecuteFormRules />
                  ) : (
                    <ExecuteFormRules
                      selectedFormSchools={selectedFormSchools}
                      onRefetch={onRefetch}
                      isBulkAction
                    />
                  )}
                </MenuGroup>
              </WithRequiredHasuraRoles>
            </>
          )}
        </RemoteDataView>
      </MenuList>
    </Menu>
  );
};
